// /invites/:token — unified redeem landing. Replaces the legacy `/c/join?inv=`
// route in lockstep with the server-side merge of `group_invite_links` into
// `group_invites`. The token here is opaque: it covers both per-email and
// anyone-with-link invites (the server resolves the hash internally).
//
// The signed-out per-email branch is now **magic-link**: server-side mints a
// one-shot Clerk ticket from the invite token, and the page consumes it so
// the recipient is authenticated + added to the group in a single click.
//
// Four branches, decided in `beforeLoad` / mount:
//
// 1. Signed-IN visitor → calls `POST /invites/{token}/accept` directly
//    (current path). Idempotent on the server (`alreadyMember=true`).
//
// 2. Signed-OUT + per-email invite (preview.invitedEmail !== null) → calls
//    `POST /invites/{token}/magic-link`. Branch on the server response:
//      • `kind: 'signin'` → consume the Clerk sign-in ticket via
//        `signIn.create({ strategy: 'ticket', ticket })`, await session,
//        then call `/accept`, then route to the group.
//      • `kind: 'signup'` → redirect to the Clerk-hosted ticket URL. Clerk
//        forwards to our /auth/signup post-exchange; the existing webhook
//        auto-redeems via `publicMetadata.pendingInviteToken`.
//    On a *transient* `/magic-link` failure (network, Clerk outage, ticket
//    hiccup) we fall back to the email-first chooser, routed by whether the
//    invitee already has an account: existing → /sign-in (sign in, then
//    bounce back here to redeem); new → /auth/signup (Clerk ticket + webhook
//    auto-redeem + role-specific onboarding). A *terminal* invite state
//    (not-found/expired/revoked/exhausted/email-mismatch, archived group)
//    surfaces an error instead — the credential flow can't repair it, and
//    bouncing the visitor through auth would just loop back to the same
//    dead invite.
//
// 3. Signed-OUT, anyone-with-link (preview.invitedEmail === null) → magic-
//    link isn't applicable (no implicit identifier). Redirect to
//    /auth/signup with the token preserved. SignupForm detects existing
//    accounts and offers a "Sign in instead" link that carries the token.
//
// 4. Fallback for unknown preview / magic-link error → /auth/signup with
//    the token in search. Same path SignupForm already handles.

import { useEffect, useRef, useState } from 'react';
import {
  createFileRoute,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useClerk, useSignIn } from '@clerk/clerk-react';
import {
  Body,
  Box,
  Button,
  Card,
  Heading,
  I,
  LoadingState,
  Row,
  Screen,
  ScreenBody,
  Stack,
} from '@leanin/ui';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { SecondFactorStep } from '@/components/auth/SecondFactorStep';
import { trackInviteAccepted } from '@/lib/analytics/events';
import { RouterLinkAs } from '@/lib/router-link';
import { failureFromError } from '@/lib/api-error';
import { navigateArgsForGroup } from '@/lib/group-url';
import { sessionQuery } from '@/lib/use-session';
import {
  fetchInvitePreview,
  requestInviteMagicLink,
  type InvitePreview,
} from '@/components/onboarding/pending-invite';
import { useAcceptInviteByToken } from '@/lib/mutations/groups';

export const Route = createFileRoute('/invites/$token')({
  beforeLoad: async ({ context, params }) => {
    let ctx;
    try {
      ctx = await context.queryClient.ensureQueryData(sessionQuery);
    } catch {
      // Treat session-fetch failures as anonymous and let the page
      // handle it — same degradation strategy as the `_public` layout.
      ctx = null;
    }

    // Always fetch the preview. Two reasons:
    //
    //   1. The signed-out branch needs `invitedEmail` to route between
    //      magic-link and the anyone-with-link fallback (today's branch).
    //
    //   2. The signed-in branch needs `invitedEmail` to render the
    //      "you're signed in as the wrong account" recovery surface
    //      with an actionable "Sign out and continue as {invitedEmail}"
    //      CTA. Without the preview, the mismatch error would dead-end
    //      with no path forward for the recipient.
    //
    // Cheap GET with public-cache semantics — same call the magic-link
    // branch already makes; running it for signed-in viewers too adds
    // one round-trip on the invite click but unlocks the full recovery UX.
    let preview: InvitePreview | null = null;
    try {
      preview = await fetchInvitePreview(params.token);
    } catch {
      preview = null;
    }

    if (ctx !== null) return { mode: 'signed-in' as const, preview };

    // Signed-out + no per-email binding: magic-link can't establish
    // identity. Route to the email-first chooser fallback at /auth/signup.
    if (preview === null || preview.invitedEmail === null) {
      throw redirect({
        to: '/auth/signup',
        search: { pendingInviteToken: params.token },
      });
    }
    return { mode: 'magic-link' as const, preview };
  },
  component: RedeemInvitePage,
});

// Server error codes mapped to user-facing copy. The accept endpoint and
// the magic-link endpoint emit the codes spelled out in the design docs;
// anything outside this set falls back to a generic message (we don't
// leak server detail to the visitor).
const INVITE_ERROR_COPY: Readonly<Record<string, string>> = {
  invite_not_found: 'This invite link isn’t valid.',
  invite_expired: 'This invite has expired. Ask the leader to send a new one.',
  invite_revoked: 'This invite has been revoked. Ask the leader to share a fresh one.',
  invite_exhausted: 'This invite has reached its use limit.',
  invite_email_mismatch:
    'This invite was sent to a different email address. Sign in with that account, or ask the leader for a new invite.',
  group_archived:
    'This group has been archived. New members can’t join until a leader restores it.',
};

// Resolved error: BOTH the user-facing copy AND the structural code, so
// the recovery surface can branch on intent (e.g. an `invite_email_mismatch`
// from a wrong-account session offers "Sign out and continue as
// {invitedEmail}"; an `invite_expired` doesn't — there's nothing the
// recipient can do client-side).
type ResolvedInviteError = {
  readonly code: keyof typeof INVITE_ERROR_COPY | 'unknown';
  readonly message: string;
};

function resolveInviteError(err: unknown): ResolvedInviteError {
  // The accept mutation throws a `DomainError`, which carries the server code
  // at `err.error.code` (not `err.code`). `failureFromError` is the canonical
  // extractor for that shape; reading `err.code` directly never matched, so
  // every failure fell through to the generic copy below.
  const { code: rawCode } = failureFromError(err, '');
  for (const [known, message] of Object.entries(INVITE_ERROR_COPY)) {
    if (rawCode.includes(known)) {
      return { code: known as keyof typeof INVITE_ERROR_COPY, message };
    }
  }
  return { code: 'unknown', message: 'This invite link isn’t valid.' };
}

function RedeemInvitePage() {
  const { token } = Route.useParams();
  const ctx = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const accept = useAcceptInviteByToken();
  const { isLoaded: clerkLoaded, signIn, setActive } = useSignIn();
  const { signOut } = useClerk();
  const [error, setError] = useState<ResolvedInviteError | null>(null);
  // When the magic-link signin transitions to needs_second_factor we
  // hand the same signIn resource to <SecondFactorStep>. After 2FA
  // verifies + setActive fires, we resume the invite redeem in
  // `onSecondFactorComplete` — no /sign-in round-trip, no password
  // re-entry, no lost flow context.
  type ClerkSignIn = NonNullable<ReturnType<typeof useSignIn>['signIn']>;
  const [secondFactorSignIn, setSecondFactorSignIn] = useState<ClerkSignIn | null>(null);

  const firedRef = useRef(false);
  const magicFiredRef = useRef(false);
  const { mutateAsync: acceptAsync } = accept;

  // Back/forward cache restore: when the recipient hits Back from
  // Clerk's hosted ticket exchange, the browser may restore this page
  // from bfcache instead of remounting it. Refs survive the restore,
  // so the effects below short-circuit and the page sits forever on
  // the LoadingState. `pageshow` with `event.persisted === true` is
  // the canonical signal for a bfcache restore — reset the guards so
  // the magic-link flow re-fires (the server's per-redeem rate limit
  // protects against abuse).
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        firedRef.current = false;
        magicFiredRef.current = false;
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    if (ctx.mode === 'signed-in') {
      // Same code path as before the magic-link rewrite: redeem against
      // the existing session and route into the target group. The accept
      // response carries only `groupId`, so the navigation target (which
      // needs the group's kind) comes from the invite preview instead.
      const target = ctx.preview;
      acceptAsync({ token })
        .then((res) => {
          // Skip the idempotent re-redeem (already a member) — the accept
          // endpoint is idempotent and this route re-arms on bfcache revisit,
          // so a reused invite link would otherwise double-count.
          if (target !== null && res.kind === 'joined' && !res.alreadyMember) {
            trackInviteAccepted({
              invite_type: target.group.kind,
              // The accept response's actual grant, not the preview's
              // roleOnAccept — the server may resolve a different role
              // (e.g. an existing member promoted by a leader invite).
              role_granted: res.role,
            });
          }
          return navigate(
            target !== null
              ? navigateArgsForGroup(target.group.kind, target.group.id)
              : { to: '/feed', search: { scope: 'all' } },
          );
        })
        .catch((err: unknown) => setError(resolveInviteError(err)));
      return;
    }

    // Magic-link branch. Needs Clerk's frontend SDK ready before we can
    // consume a sign-in ticket. Wait for `clerkLoaded` to flip via a
    // dependent effect below — re-running once `signIn`/`setActive` are
    // populated. Guard with a separate ref to keep this initial mount
    // effect a no-op for the magic-link case.
  }, [acceptAsync, ctx, navigate, token]);

  // Magic-link branch: wait for Clerk to be ready, then mint the ticket
  // and consume it. Separate effect so the dependency array can include
  // the Clerk-loaded gate without re-entering the signed-in branch.
  // `magicFiredRef` is declared above (shared with the bfcache-restore
  // listener so a Back-from-Clerk navigation re-fires this flow).
  useEffect(() => {
    if (magicFiredRef.current) return;
    if (ctx.mode !== 'magic-link') return;
    if (!clerkLoaded || signIn === undefined || setActive === undefined) return;
    magicFiredRef.current = true;

    // Narrowed to the magic-link member by the guard above; capture so the
    // closure keeps `preview` non-null when picking the fallback route.
    const { preview } = ctx;

    void (async () => {
      try {
        const result = await requestInviteMagicLink(token);
        if (result.kind === 'signup') {
          // Hand off to Clerk's hosted ticket exchange. Clerk forwards
          // to our /auth/signup post-exchange; the existing
          // user.created webhook auto-redeems via the metadata token.
          // No invite_accepted fires on this path: the redemption runs
          // server-side in that webhook, so there is no client success
          // continuation to instrument.
          window.location.assign(result.redirectUrl);
          return;
        }
        // kind === 'signin' → consume the Clerk sign-in ticket.
        const attempt = await signIn.create({
          strategy: 'ticket',
          ticket: result.signInToken,
        });
        if (attempt.status === 'needs_second_factor') {
          // Recipient's account has 2FA enabled. Hand off to the shared
          // SecondFactorStep flow in-place: the ticket-bound signIn
          // resource carries the first-factor completion, and the
          // SecondFactorStep finishes verification + setActive against
          // the SAME resource. On success the onComplete callback
          // resumes the invite redeem right here without a round-trip
          // to /sign-in.
          setSecondFactorSignIn(signIn);
          return;
        }
        if (attempt.status !== 'complete' || !attempt.createdSessionId) {
          throw new Error(`magic_link_signin_${attempt.status}`);
        }
        await setActive({ session: attempt.createdSessionId });
        // Same race the /auth/finishing path closes: the unauthenticated
        // hit on this route cached a `null` session, and after `setActive`
        // the next route guard's `ensureQueryData(sessionQuery)` will read
        // that cached null and bounce to /sign-in unless we force a
        // refetch FIRST. `refetchQueries` invalidates AND awaits the
        // in-flight fetch — `invalidateQueries` only marks stale and lets
        // the next reader race the Clerk session-cookie's propagation.
        await queryClient.refetchQueries({ queryKey: sessionQuery.queryKey });
        // Session is now live — redeem the group invite against it. The
        // accept response carries only `groupId`, so the navigation target
        // (which needs the group's kind) comes from the invite preview.
        const res = await acceptAsync({ token });
        if (preview !== null && res.kind === 'joined' && !res.alreadyMember) {
          trackInviteAccepted({
            invite_type: preview.group.kind,
            role_granted: res.role,
          });
        }
        navigate(
          preview !== null
            ? navigateArgsForGroup(preview.group.kind, preview.group.id)
            : { to: '/feed', search: { scope: 'all' } },
        );
      } catch (err) {
        // Decide on the server error CODE, never on translated copy. The
        // keys of INVITE_ERROR_COPY *are* the terminal invite states — an
        // invite that's not-found / expired / revoked / exhausted /
        // email-mismatched, or an archived group. Those are honest
        // dead-ends the credential flow can't repair, so surface them.
        // (Matching on copy was broken: the default fell back to the same
        // string as `invite_not_found`, so every failure looked terminal
        // *and* every failure looked like not-found — the guard could never
        // distinguish them.)
        const { code } = failureFromError(err, '');
        const isTerminal = Object.keys(INVITE_ERROR_COPY).some((known) =>
          code.includes(known),
        );
        if (isTerminal) {
          setError(resolveInviteError(err));
          return;
        }
        // Transient failure (Clerk ticket hiccup, network, unknown). Degrade
        // to the email-first chooser, routed by whether the invitee already
        // has an account: existing → /sign-in (sign in, then bounce back
        // here to redeem via the signed-in branch); new → /auth/signup
        // (Clerk ticket + `user.created` webhook auto-redeem + the
        // role-specific onboarding tour). Routing a *new* invitee to
        // /sign-in stranded them on a login form for an account that
        // doesn't exist — and the leadership the invite carried was never
        // granted.
        navigate({
          to: preview.inviteeHasAccount ? '/sign-in' : '/auth/signup',
          search: { pendingInviteToken: token },
          replace: true,
        });
      }
    })();
  }, [ctx, clerkLoaded, signIn, setActive, token, acceptAsync, navigate, queryClient]);

  // 2FA branch of the magic-link signin. The shared SecondFactorStep
  // owns strategy selection + prepare + attempt + setActive against
  // the signIn resource we hand it. On success, redeem the invite
  // against the now-live session and route to the group.
  if (secondFactorSignIn !== null && setActive !== undefined) {
    return (
      <AuthLayout
        kicker="Almost there"
        headline={`Verify it’s you to join ${ctx.preview?.group.name ?? 'the group'}.`}
      >
        <SecondFactorStep
          signIn={secondFactorSignIn}
          setActive={setActive}
          onComplete={async () => {
            try {
              // Same race the /auth/finishing path closes — see the
              // comment in the signin branch above. SecondFactorStep
              // calls setActive on the same signIn resource, so the
              // unauth'd cache from this page's initial load needs a
              // forced refetch BEFORE we drive any authed navigation.
              await queryClient.refetchQueries({ queryKey: sessionQuery.queryKey });
              const res = await acceptAsync({ token });
              if (
                ctx.preview !== null &&
                res.kind === 'joined' &&
                !res.alreadyMember
              ) {
                trackInviteAccepted({
                  invite_type: ctx.preview.group.kind,
                  role_granted: res.role,
                });
              }
              navigate(
                ctx.preview !== null
                  ? navigateArgsForGroup(
                      ctx.preview.group.kind,
                      ctx.preview.group.id,
                    )
                  : { to: '/feed', search: { scope: 'all' } },
              );
            } catch (err) {
              setError(resolveInviteError(err));
              setSecondFactorSignIn(null);
            }
          }}
        />
      </AuthLayout>
    );
  }

  // Already-accepted is NOT a dead-end. A single-use invite that's been
  // redeemed almost always means the recipient is coming back — they have
  // an account and (for leader/member invites) are already in the group.
  // The old path surfaced this as a terminal "reached its use limit" error
  // that pushed signed-out visitors out to the marketing site. Instead,
  // acknowledge it succeeded and usher them to sign-in. (Intercepts before
  // the generic terminal-error card below; `invite_exhausted` stays in the
  // terminal set so the magic-link catch doesn't degrade it to /auth/signup.)
  if (error?.code === 'invite_exhausted') {
    const groupName = ctx.preview?.group.name ?? null;
    // Plain Stack in the AuthLayout form slot + full-width primary button —
    // the convention every auth screen uses (login / signup / finishing /
    // RecoveryError). No inner Card or width Box: AuthLayout owns the
    // surface and the headline.
    return (
      <AuthLayout kicker="Invitation" headline="You’re already in.">
        <Stack gap="lg">
          <Heading level={1} style={{ margin: 0 }}>
            This invitation has already been accepted.
          </Heading>
          <Body color="mute">
            {groupName
              ? `You’ve already accepted your invitation to ${groupName}. Sign in to pick up where you left off.`
              : 'You’ve already accepted this invitation. Sign in to continue.'}
          </Body>
          <Button
            variant="primary"
            block
            iconRight={I.arrow}
            href="/sign-in"
            linkAs={RouterLinkAs}
          >
            Sign in to continue
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  if (error !== null) {
    // Recovery actions are conditional on the error code AND the visitor's
    // auth state. The previous shape rendered the same "Back to feed /
    // Browse Circles" pair for every error — useless for a brand-new
    // invitee (who has no account, so /feed bounces to /sign-in) and
    // missing the actionable path for the wrong-account-signed-in case.
    const invitedEmail =
      ctx.preview !== null ? ctx.preview.invitedEmail : null;
    const onSignOutAndRetry = async () => {
      // Two things to clear, then a hard reload onto the invite URL:
      //
      //   1. Clerk's session cookies via `signOut()`. In a real prod
      //      session this is the only sign-out that matters; Clerk's
      //      built-in redirect does a `window.location.assign` itself.
      //   2. The `dev_user_id` bypass cookie. Clerk doesn't know about
      //      this — in dev it's the actual authenticator, and signOut
      //      is a no-op against it. Clearing it explicitly makes "sign
      //      out" mean "sign out" in dev. No-op in prod where the
      //      cookie doesn't exist.
      //
      // The trailing `window.location.assign` is the belt-and-suspenders
      // step: in prod Clerk's own redirect lands first, and in dev (where
      // Clerk's redirect is skipped because there's no real session) we
      // force the hard reload ourselves so the route's beforeLoad fires
      // freshly against the now-anonymous viewer.
      document.cookie = 'dev_user_id=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
      await signOut();
      window.location.assign(`/invites/${token}`);
    };
    const isMismatchInSession =
      error.code === 'invite_email_mismatch' && ctx.mode === 'signed-in';

    // Wrap the recovery card in AuthLayout for the branded surface every
    // other auth-adjacent page uses (login, signup, finishing). Prior
    // shape was a bare Screen with a card floating on the beige
    // background — no Lean In wordmark anywhere — looked like an error
    // page from somewhere else.
    return (
      <AuthLayout
        kicker={isMismatchInSession ? 'Account check' : 'Invitation'}
        headline={
          isMismatchInSession
            ? 'Switch to the right account to continue.'
            : 'There’s a problem with this link.'
        }
      >
          {/* The recovery card must survive mobile viewports: fixed
            * maxWidth was fine for desktop but produced horizontal
            * overflow at 390px because the parent had no padding and
            * the card itself didn't reflow. `width: '100%'` + a max
            * + horizontal page padding via Box.px keep it readable
            * everywhere. */}
          <Box
            px="md"
            style={{
              width: '100%',
              maxWidth: 520,
              margin: '0 auto',
              boxSizing: 'border-box',
            }}
            data-testid="invite-error"
            data-invite-error-reason={error.code}
          >
            <Card pad="default">
              <Stack gap="lg">
                <Heading level={2}>
                  {isMismatchInSession
                    ? 'You’re signed in with a different account.'
                    : 'We couldn’t accept this invite.'}
                </Heading>
                <Body color="mute">
                  {isMismatchInSession && invitedEmail !== null
                    ? `This invite was sent to ${invitedEmail}. Sign out and sign back in as ${invitedEmail}, or ask the leader for a fresh invite.`
                    : error.message}
                </Body>
                <Row gap="sm" justify="space-between" wrap>
                  {isMismatchInSession ? (
                    <>
                      <Button
                        variant="ghost"
                        size="md"
                        href="/feed"
                        linkAs={RouterLinkAs}
                      >
                        Stay signed in
                      </Button>
                      <Button
                        variant="primary"
                        size="md"
                        iconRight={I.arrow}
                        onClick={() => void onSignOutAndRetry()}
                      >
                        Sign out and continue
                      </Button>
                    </>
                  ) : ctx.mode === 'signed-in' ? (
                    <Button
                      variant="primary"
                      size="md"
                      iconRight={I.arrow}
                      href="/feed"
                      linkAs={RouterLinkAs}
                    >
                      Back to feed
                    </Button>
                  ) : (
                    // Signed-out + terminal error: there's no useful place
                    // to send them inside the app (they have no account).
                    // Surface a single muted CTA that points back to the
                    // marketing site — the inviter can re-share once the
                    // problem is fixed on their end.
                    <Button
                      variant="primary"
                      size="md"
                      iconRight={I.arrow}
                      href="https://leanin.org"
                    >
                      About Lean In
                    </Button>
                  )}
                </Row>
              </Stack>
            </Card>
          </Box>
      </AuthLayout>
    );
  }

  const loadingLabel =
    ctx.mode === 'magic-link'
      ? `Signing you in to ${ctx.preview.group.name}`
      : 'Accepting your invite';

  return (
    <Screen>
      <ScreenBody>
        <LoadingState placement="page" label={loadingLabel} />
      </ScreenBody>
    </Screen>
  );
}
